import { useOffer } from "@nju/data-access-portal-offers";
import { IFSecure, IServiceStatus } from "@nju/data-access-portal-product";
import { Icon } from "@nju/icons";
import {
  BlockText,
  IconListElement,
  Markdown,
  Panel,
  Separator,
  Text,
} from "@nju/ui";
import { formatDate } from "@nju/utils";

interface IFSecureOrderCompletedStatusProps {
  fSecure: IFSecure | undefined;
  fSecureServiceStatus: IServiceStatus["fSecureServiceStatus"];
}

export function FSecureOrderCompletedStatus({
  fSecure,
  fSecureServiceStatus,
}: IFSecureOrderCompletedStatusProps) {
  const isActive = fSecureServiceStatus === "Active";
  const isPendingActive = fSecureServiceStatus === "PendingActive";
  const offer = useOffer(fSecure?.characteristic.productVariant);

  return (
    <div className="flex flex-col gap-4">
      <Panel
        iconName="shield-check"
        isFoldable={true}
        title="Bezpieczny internet"
      >
        {({ isExpanded }) => {
          return isExpanded ? (
            <>
              <Text
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "l",
                }}
              >
                Twoja ochrona przed wirusami i niebezpiecznymi stronami na
                telefonach i innych urządzeniach
              </Text>
              <Separator size="m" />
              <div className="flex items-center gap-3">
                <Icon
                  className="w-[32px] h-[32px]"
                  name="router"
                  tone="accent"
                />
                <Text
                  size={{
                    "@onlyMobile": "l",
                    "@fromTablet": "xl",
                  }}
                  weight="bold"
                >
                  oferta
                </Text>
              </div>
              <Separator size="s" />
              {offer?.category === "FSECURE" && (
                <>
                  <Text
                    size={{
                      "@onlyMobile": "m",
                      "@fromTablet": "l",
                    }}
                    weight="bold"
                  >
                    {offer?.displayName?.[0]?.additionalDescription?.pl}
                  </Text>
                  <Separator size="xxs" tone="invisible" />
                  {offer?.activeServiceFeatures?.map((feature, index) => {
                    return (
                      <Markdown
                        className="prose prose-img:m-0 prose-h2:mt-[1em] text-size-14 from-tablet:text-size-16 list-disc marker:text-cerulean"
                        content={feature.description.pl}
                        key={index}
                      />
                    );
                  })}
                </>
              )}
              <Separator size="s" tone="invisible" />
              <Separator size="m" />
              <BlockText
                className="mb-3"
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "l",
                }}
                weight="bold"
              >
                Zarządzaj usługą w aplikacji zainstalowanej na Twoim urządzeniu
              </BlockText>
              <IconListElement
                borderStyle="dashed"
                iconName="phone-laptop"
                iconSize="medium"
                mode="raw"
                text="Aby rozpocząć, przejdź na stronę Bezpiecznego internetu i zaloguj się na swoje konto."
              />
              <div className="flex gap-2 items-start mt-10">
                <div className="w-4 h-4">
                  <Icon
                    height={20}
                    name="info-circle"
                    tone="accent"
                    width={20}
                  />
                </div>
                <Text size="m">
                  Jeśli nie masz jeszcze konta, założysz je w kilku prostych
                  krokach. Sprawdź swoją skrzynkę e-mail i kliknij w link
                  aktywacyjny, który do Ciebie wysłaliśmy.
                </Text>
              </div>
            </>
          ) : (
            <Text
              className="whitespace-pre-line"
              size={{
                "@onlyMobile": "m",
                "@fromTablet": "l",
              }}
            >
              Twoja ochrona przed wirusami i niebezpiecznymi stronami na
              telefonach i innych urządzeniach
            </Text>
          );
        }}
      </Panel>
      <Panel>
        <div className="flex only-mobile:items-center">
          <Icon
            className="w-[27px] h-[24px] mr-3 from-tablet:w-[32px] from-tablet:h-[29px]"
            name="wallet"
            tone="accent"
          />
          <Text
            size={{
              "@onlyMobile": "m",
              "@fromTablet": "xl",
            }}
            weight="bold"
          >
            najbliższa płatność
          </Text>
        </div>
        <Separator size="s" />
        {isActive || isPendingActive ? (
          fSecure?.anniversaryDate && (
            <Text size="s" tone="accent">
              {formatDate(fSecure.anniversaryDate, false)}
            </Text>
          )
        ) : (
          <Text
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "m",
            }}
            tone="accent"
          >
            opłata czasowo zawieszona
          </Text>
        )}
        {fSecure?.anniversaryDate &&
          fSecure?.recurringPrice !== undefined &&
          offer?.category === "FSECURE" && (
            <div className="flex justify-between">
              <Text
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "l",
                }}
                weight="bold"
              >
                opłata {offer?.displayName?.[0]?.additionalDescription?.pl}
              </Text>
              <Text
                className="whitespace-nowrap"
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "l",
                }}
                weight="bold"
              >
                {`${fSecure?.recurringPrice} PLN`}
              </Text>
            </div>
          )}
        {isActive && (
          <>
            <Separator size="xs" />
            <div className="flex items-start from-tablet:items-center">
              <Text
                size={{
                  "@onlyMobile": "s",
                  "@fromTablet": "m",
                }}
              >
                usługa odnawia się automatycznie co miesiąc
              </Text>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}
